import SelectWithLabel from "@/components/form/hook-form/select-with-label";
import TextFieldWithLabel from "@/components/form/hook-form/textfield-with-label";
import { useEffect } from "react";
import { FieldValues, Path, PathValue, UseFormReturn, useWatch } from "react-hook-form";

type WeightInputUnitFieldsProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  valueName: Path<T>;
  unitName: Path<T>;
  valueError?: string;
  unitError?: string;
};

const WEIGHT_UNIT_OPTIONS: { value: string; name: string }[] = [
  { value: "kg", name: "kg" },
  { value: "lbs", name: "lbs" },
];

const sanitizeWeightInput = (value: string) => {
  const normalizedSeparator = value.replace(/,/g, ".");
  const numericOnly = normalizedSeparator.replace(/[^\d.]/g, "");
  if (!numericOnly) return "";

  const [integerPart, ...decimalParts] = numericOnly.split(".");
  const hasDecimalPoint = decimalParts.length > 0;
  const firstDecimalDigit = decimalParts.join("").replace(/\./g, "").slice(0, 1);

  if (!hasDecimalPoint) return integerPart;
  if (!firstDecimalDigit) return `${integerPart || "0"}.`;

  return `${integerPart || "0"}.${firstDecimalDigit}`;
};

export const WeightInputUnitFields = <T extends FieldValues>({
  form,
  valueName,
  unitName,
  valueError,
  unitError,
}: WeightInputUnitFieldsProps<T>) => {
  const watchedWeight = useWatch({
    control: form.control,
    name: valueName,
  }) as string;

  useEffect(() => {
    const normalizedWeight = sanitizeWeightInput(watchedWeight ?? "");
    if (normalizedWeight === (watchedWeight ?? "")) return;

    form.setValue(valueName, normalizedWeight as PathValue<T, Path<T>>, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [form, valueName, watchedWeight]);

  return (
    <div className="flex w-full gap-3">
      <TextFieldWithLabel
        control={form.control}
        name={valueName}
        label="Weight"
        placeholder="Add weight (e.g. 52.5)"
        type="text"
        inputMode="decimal"
        error={valueError}
        containerClassName="flex-1"
        inputClassName="rounded-[14px] px-3"
      />

      <SelectWithLabel
        control={form.control}
        name={unitName}
        label="Weight Unit"
        placeholder="Choose..."
        options={WEIGHT_UNIT_OPTIONS}
        selectTitle="Select weight unit"
        error={unitError}
        containerClassName="flex-1"
      />
    </div>
  );
};

export default WeightInputUnitFields;
